import { Zap } from "lucide-react";

/** Calendar Friday expiries through `horizon` DTE (weekly + third-Friday monthly). */
export function weeklyFridayExpirations(horizon = 90) {
  const ticks = [];
  const now = new Date();
  const utc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const wd = new Date(utc).getUTCDay();
  let add = (5 - wd + 7) % 7;
  if (add === 0) add = 7;
  for (let i = 0; i < 20; i++) {
    const ms = utc + (add + i * 7) * 86400000;
    const dte = Math.round((ms - utc) / 86400000);
    if (dte < 1 || dte > horizon) continue;
    const d = new Date(ms);
    const iso = d.toISOString().slice(0, 10);
    const day = d.getUTCDate();
    const dm = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
    ticks.push({
      date: iso,
      dte,
      days: dte,
      monthly: day >= 15 && day <= 21,
      friday: true,
      label: `Fri ${dm} · ${dte} DTE`,
    });
  }
  return ticks;
}

export function closestFridayDte(target = 38, horizon = 90) {
  const ticks = weeklyFridayExpirations(horizon);
  if (!ticks.length) return target;
  return ticks.reduce((best, t) => (
    Math.abs(t.dte - target) < Math.abs(best.dte - target) ? t : best
  )).dte;
}

function chipInfo(e, { regimeP75, regimeAllowsPremium } = {}) {
  const p75 = regimeP75 || 30;
  if (e.crosses_earnings) return { dot: "bg-red-500", badge: "EARN", color: "#EF4444", tip: "Expires after earnings — IV crush risk. Premium may collapse post-event." };
  if (e.dte > p75) return { dot: "bg-orange-500", badge: "LONG", color: "#F97316", tip: `DTE exceeds regime P75 sojourn (${p75}d). Regime may shift before expiry.` };
  if (regimeAllowsPremium === false && e.dte >= 7) return { dot: "bg-zinc-500", badge: null, color: "#71717A", tip: "Regime blocks premium-selling. Use regime-aligned strategies." };
  if (e.premium_yield != null && e.premium_yield < 0.5 && e.dte >= 7) return { dot: "bg-amber-500", badge: "LOW$", color: "#F59E0B", tip: "Premium below 0.5% yield threshold. Move to shorter DTE or wider spread." };
  if (e.dte >= 35 && e.dte <= 47 && e.monthly) return { dot: "bg-emerald-500", badge: "WHEEL", color: "#10B981", tip: "Standard Wheel income — monthly expiry preferred in 35–45 DTE." };
  if (e.dte >= 18 && e.dte <= 25) return { dot: "bg-emerald-500", badge: "ROLL", color: "#10B981", tip: "Roll management — nearest listed expiry around 21 DTE." };
  if (e.dte >= 7 && e.dte <= 14) return { dot: "bg-blue-500", badge: "SHORT", color: "#3B82F6", tip: "Short-term income — listed Friday in 7–14 DTE." };
  if (e.dte >= 7 && e.dte <= 21 && (e.premium_yield == null || e.premium_yield >= 0.5) && regimeAllowsPremium !== false) {
    return { dot: "bg-zinc-500", badge: null, color: "#71717A", tip: "Listed weekly — not the standard 35–45 DTE Wheel income window." };
  }
  if (e.dte < 7) return { dot: "bg-blue-500", badge: "SCALP", color: "#3B82F6", tip: "Very short DTE — gamma risk elevated. Directional plays only." };
  if (e.monthly) return { dot: "border-2 border-white bg-transparent", badge: "MTHLY", color: "#A1A1AA", tip: "Monthly expiry — highest liquidity and open interest." };
  return { dot: "bg-zinc-500", badge: null, color: "#71717A", tip: "Standard weekly expiry." };
}

function fmt(d) {
  try { return new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return d; }
}

function fmtFull(d) {
  try { return new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export function ExpiryChips({
  expirations, selectedExpiry, setSelectedExpiry, bestEntry, regimeP75, regimeAllowsPremium,
  chipLimit = 16,
}) {
  if (!expirations || expirations.length === 0) return null;
  const selected = expirations.find((e) => e.date === selectedExpiry);
  const selInfo = selected ? chipInfo(selected, { regimeP75, regimeAllowsPremium }) : null;
  const shown = chipLimit ? expirations.slice(0, chipLimit) : expirations;

  return (
    <div className="space-y-1.5" data-testid="expiry-chips">
      {selected && (
        <div className="flex items-center gap-2 text-[10px] font-mono-data" data-testid="selected-expiry-summary">
          <span style={{ color: "var(--hoip-text-primary)" }}>{fmtFull(selected.date)}</span>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>{selected.dte}d to expiry</span>
          {selInfo?.badge && (
            <span className="px-1 py-0.5 rounded-sm text-[8px] font-bold" style={{ color: selInfo.color, background: `${selInfo.color}15` }}>
              {selInfo.badge}
            </span>
          )}
          {selected.crosses_earnings && <span className="text-red-400 text-[9px]">Earnings cross</span>}
        </div>
      )}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin" data-testid="expiry-chip-row">
        {shown.map((e) => {
          const isActive = e.date === selectedExpiry;
          const info = chipInfo(e, { regimeP75, regimeAllowsPremium });
          return (
            <button key={e.date} type="button" onClick={() => setSelectedExpiry(e.date)} data-testid={`expiry-chip-${e.date}`}
              title={info.tip}
              className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono-data border rounded-sm shrink-0 transition-all cursor-pointer ${
                isActive ? "border-blue-500 bg-blue-500/10" : "border-[var(--hoip-border)] hover:border-[var(--hoip-text-tertiary)]"
              }`} style={{ color: isActive ? "#3B82F6" : "var(--hoip-text-secondary)" }}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${info.dot}`} />
              <span className={isActive ? "font-bold" : ""}>{fmt(e.date)}</span>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>{e.dte}d</span>
              {info.badge && <span className="text-[7px] font-bold" style={{ color: info.color }}>{info.badge}</span>}
            </button>
          );
        })}
      </div>
      {bestEntry && (
        <button type="button" onClick={() => bestEntry.date && setSelectedExpiry(bestEntry.date)} data-testid="best-entry-row"
          className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[10px] border rounded-sm transition-colors ${bestEntry.date ? "cursor-pointer hover:bg-[var(--hoip-hover)]" : "cursor-default"}`}
          style={{
            borderColor: bestEntry.no_entry ? "rgba(245,158,11,0.3)" : (bestEntry.regime_aligned ? "rgba(16,185,129,0.3)" : "rgba(59,130,246,0.3)"),
            background: bestEntry.no_entry ? "rgba(245,158,11,0.04)" : (bestEntry.regime_aligned ? "rgba(16,185,129,0.04)" : "rgba(59,130,246,0.04)"),
            color: bestEntry.no_entry ? "#F59E0B" : (bestEntry.regime_aligned ? "#10B981" : "#3B82F6"),
          }}>
          <Zap className="w-3 h-3 shrink-0" />
          <span className="font-mono-data">{bestEntry.label}</span>
        </button>
      )}
    </div>
  );
}

export function DTETimeline({
  availableExpirations, selectedExpiry, onSelectExpiry, daysToEarnings, earningsDate,
  earningsPassed, preparingCopy, labelLimit = 16,
}) {
  const today = new Date();
  const rawExpiries = availableExpirations || [];
  const expiries = rawExpiries.map((e) => {
    const dateStr = typeof e === "string" ? e : e.date;
    const dteGiven = typeof e === "object" ? (e.dte ?? e.days) : null;
    const d = new Date(`${dateStr}T00:00:00`);
    const days = dteGiven != null ? dteGiven : Math.round((d - today) / 86400000);
    const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
    const dm = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
    const label = (typeof e === "object" && e.label) || `${weekday} ${dm} · ${days} DTE`;
    const friday = typeof e === "object" && e.friday != null ? e.friday : d.getDay() === 5;
    const monthly = typeof e === "object" ? !!e.monthly : false;
    return { date: dateStr, days, dte: days, label, monthly, friday };
  }).filter((e) => e.days > 0 && e.date).sort((a, b) => a.days - b.days);

  if (!expiries.length) {
    return (
      <div className="space-y-1" data-testid="dte-timeline">
        <div className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="dte-preparing">
          {preparingCopy || "Expiration snapshot preparing"}
        </div>
      </div>
    );
  }

  const selExp = selectedExpiry ? expiries.find((e) => e.date === selectedExpiry) : null;
  const maxDte = Math.max(...expiries.map((e) => e.days), (daysToEarnings && daysToEarnings > 0) ? daysToEarnings : 0, 7);
  const pct = (d) => Math.min(Math.max((d / maxDte) * 100, 2), 98);
  const labels = expiries.filter((e) => e.friday || e.monthly);
  const shownLabels = labelLimit ? labels.slice(0, labelLimit) : labels;

  return (
    <div className="space-y-1" data-testid="dte-timeline">
      <div className="flex justify-between text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span>Today</span>
        <span>{maxDte}d</span>
      </div>
      <div className="relative h-10 rounded-sm" style={{ background: "var(--hoip-bg)", border: "1px solid var(--hoip-border)" }}>
        {daysToEarnings != null && daysToEarnings > 0 && daysToEarnings <= maxDte && !earningsPassed && (
          <div className="absolute top-0 bottom-0 w-0.5 z-10" style={{ left: `${pct(daysToEarnings)}%`, background: "#EF4444" }}>
            <div className="absolute -top-0.5 -left-1.5 text-[8px] font-mono-data text-red-400 whitespace-nowrap">E</div>
          </div>
        )}
        {expiries.map((e) => {
          const active = e.date === selectedExpiry;
          return (
            <button
              key={e.date}
              type="button"
              data-testid={`dte-tick-${e.date}`}
              title={e.label}
              onClick={() => onSelectExpiry && onSelectExpiry(e.date)}
              className="absolute top-1 flex flex-col items-center -translate-x-1/2 cursor-pointer"
              style={{ left: `${pct(e.days)}%` }}
            >
              <span className="block rounded-full"
                style={{
                  width: active ? 10 : 7,
                  height: active ? 10 : 7,
                  background: active ? "#3B82F6" : (e.friday ? "#94A3B8" : "#64748B"),
                  boxShadow: active ? "0 0 0 2px rgba(59,130,246,0.35)" : "none",
                }} />
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 text-[9px] flex-wrap" data-testid="dte-tick-labels">
        {shownLabels.map((e) => (
          <button key={`lbl-${e.date}`} type="button"
            onClick={() => onSelectExpiry && onSelectExpiry(e.date)}
            className={`font-mono-data ${e.date === selectedExpiry ? "font-bold text-blue-400" : ""}`}
            style={{ color: e.date === selectedExpiry ? "#3B82F6" : "var(--hoip-text-tertiary)" }}>
            {e.label}
          </button>
        ))}
      </div>
      <div className="flex gap-3 text-[9px] flex-wrap">
        {selExp && <span className="font-bold text-blue-400" data-testid="dte-selected-label">Selected: {selExp.label}</span>}
        {earningsDate && !earningsPassed && daysToEarnings != null && daysToEarnings > 0 && (
          <span className="text-red-400">Earn: {earningsDate} ({daysToEarnings}d)</span>
        )}
      </div>
    </div>
  );
}
